import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * "Complete the look" rail.
 *
 * - On the product page the rail is server-rendered; this adds arrows and size buttons.
 * - In the cart drawer it starts as a placeholder with `data-src` (the latest cart product rendered
 *   through sections/mala-complete-look.liquid); it loads that, drops products already in the cart
 *   (`data-exclude`) and reloads whenever the cart drawer re-renders with new contents.
 * - Size buttons add the variant through /cart/add.js and announce it with the standard
 *   CartLinesUpdateEvent, so Horizon's cart drawer and bubble update exactly as with the product form.
 */
class MalaCompleteLook extends HTMLElement {
  static observedAttributes = ['data-src', 'data-exclude'];

  /** @type {AbortController | undefined} */
  #abort;
  /** @type {AbortController | undefined} */
  #load;
  #loadedKey = '';
  /** Last rail rendered for #loadedKey, restored when the cart drawer re-render resets our children. */
  #cachedHtml = '';
  /** @type {MutationObserver | undefined} */
  #observer;
  #restoring = false;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('click', this.#onClick, { signal });
    this.#bindTrack();
    this.#maybeLoad();

    if (this.dataset.src) {
      this.#observer = new MutationObserver(this.#onChildrenReplaced);
      this.#observer.observe(this, { childList: true });
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#load?.abort();
    this.#observer?.disconnect();
  }

  /** The cart drawer morph swaps our loaded rail back to the placeholder; put the cached rail back. */
  #onChildrenReplaced = () => {
    if (this.#restoring || this.classList.contains('is-loading') || this.querySelector('.mala-look__item')) return;

    const key = `${this.dataset.src}|${this.dataset.exclude ?? ''}`;
    if (key !== this.#loadedKey || !this.#cachedHtml) {
      this.#maybeLoad();
      return;
    }

    this.#restoring = true;
    this.innerHTML = this.#cachedHtml;
    this.hidden = false;
    this.#bindTrack();
    queueMicrotask(() => (this.#restoring = false));
  };

  attributeChangedCallback() {
    if (this.isConnected) this.#maybeLoad();
  }

  get #track() {
    return this.querySelector('.mala-look__track');
  }

  #bindTrack() {
    const track = this.#track;
    if (!(track instanceof HTMLElement) || !this.#abort) return;

    track.addEventListener('scroll', () => this.#updateArrows(), { passive: true, signal: this.#abort.signal });
    this.#updateArrows();
  }

  #updateArrows() {
    const track = this.#track;
    if (!(track instanceof HTMLElement)) return;

    const max = track.scrollWidth - track.clientWidth;
    const position = Math.abs(track.scrollLeft);
    this.toggleAttribute('data-static', max <= 2);

    const previous = this.querySelector('[data-look-prev]');
    const next = this.querySelector('[data-look-next]');
    if (previous instanceof HTMLButtonElement) previous.disabled = position <= 2;
    if (next instanceof HTMLButtonElement) next.disabled = position >= max - 2;
  }

  async #maybeLoad() {
    const src = this.dataset.src;
    if (!src) return;

    const key = `${src}|${this.dataset.exclude ?? ''}`;
    if (key === this.#loadedKey) return;
    this.#loadedKey = key;

    this.#load?.abort();
    this.#load = new AbortController();
    this.classList.add('is-loading');

    try {
      const response = await fetch(src, { signal: this.#load.signal });
      if (!response.ok) throw new Error(`Complete the look request failed: ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = html.querySelector('mala-complete-look');
      const exclude = new Set((this.dataset.exclude ?? '').split(',').filter(Boolean));
      const limit = Number(this.dataset.limit || fresh?.getAttribute('data-limit') || 6);

      if (!fresh) {
        this.hidden = true;
        return;
      }

      let kept = 0;
      for (const item of fresh.querySelectorAll('.mala-look__item')) {
        const id = item.getAttribute('data-product-id') ?? '';
        if (exclude.has(id) || kept >= limit) item.remove();
        else kept += 1;
      }

      if (kept === 0) {
        this.hidden = true;
        return;
      }

      this.#restoring = true;
      this.replaceChildren(...fresh.childNodes);
      this.#cachedHtml = this.innerHTML;
      queueMicrotask(() => (this.#restoring = false));
      this.hidden = false;
      this.#bindTrack();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      this.hidden = true;
      console.error(error);
    } finally {
      this.classList.remove('is-loading');
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const arrow = event.target.closest('[data-look-prev], [data-look-next]');
    if (arrow) {
      this.#scroll(arrow.hasAttribute('data-look-next') ? 1 : -1);
      return;
    }

    const size = event.target.closest('button[data-variant-id]');
    if (size instanceof HTMLButtonElement && !size.disabled) this.#add(size);
  };

  /** @param {number} direction */
  #scroll(direction) {
    const track = this.#track;
    const item = track?.firstElementChild;
    if (!(track instanceof HTMLElement) || !(item instanceof HTMLElement)) return;

    const rtl = getComputedStyle(this).direction === 'rtl';
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollBy({ left: direction * (item.offsetWidth + 12) * (rtl ? -1 : 1), behavior: reduced ? 'auto' : 'smooth' });
  }

  /** @param {HTMLButtonElement} button */
  async #add(button) {
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const status = this.querySelector('[data-look-status]');
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((component) => (component instanceof HTMLElement ? component.dataset.sectionId : ''))
      .filter(Boolean);

    button.setAttribute('aria-busy', 'true');
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const body = new FormData();
      body.set('id', variantId);
      body.set('quantity', '1');
      if (sectionIds.length) body.set('sections', sectionIds.join(','));

      const response = await fetch(window.Theme?.routes?.cart_add_url ?? '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
      }).then((res) => res.json());

      const ajaxCart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((res) => res.json());
      const failed = Boolean(response.status);

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'mala-complete-look',
          sourceId: variantId,
          itemCount: 1,
          sections: response.sections,
          didError: failed,
        },
      });

      if (failed) {
        this.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' }));
        if (status) status.textContent = response.description || response.message || '';
        return;
      }

      button.classList.add('is-added');
      if (status) status.textContent = button.getAttribute('aria-label') ?? '';
    } catch (error) {
      deferred.reject(error);
      console.error(error);
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('mala-complete-look')) customElements.define('mala-complete-look', MalaCompleteLook);
